import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ClipboardCheck, Wrench, ShoppingBag, GraduationCap, Landmark, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import cityBg from "@/assets/city-bg-hero.webp";

// Preload hero background as early as possible to reduce LCP render delay.
// Injected at module evaluation so it kicks off before React mounts.
if (typeof document !== "undefined" && !document.head.querySelector('link[data-preload="hero-bg"]')) {
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = cityBg;
  link.type = "image/webp";
  link.setAttribute("fetchpriority", "high");
  link.setAttribute("data-preload", "hero-bg");
  document.head.appendChild(link);
}

interface HeroSectionProps {
  onSignIn: () => void;
  onSignUp?: () => void;
}

const prefetchers: Record<string, () => Promise<unknown>> = {
  "/assessments": () => import("@/pages/Assessments"),
  "/services": () => import("@/pages/Services"),
  "/marketplace": () => import("@/pages/Marketplace"),
  "/expert-advisory": () => import("@/pages/ExpertAdvisory"),
  "/testbed-hub": () => import("@/pages/testbed/TestbedHubHome"),
  "/assessments/new": () => import("@/pages/CreateAssessment"),
  "/explore": () => import("@/pages/ExplorePlatform"),
};

const prefetch = (href: string) => {
  const fn = prefetchers[href];
  if (fn) fn().catch(() => {});
};

const journeySteps = [
  { icon: ClipboardCheck, label: "Age-Friendly Index", desc: "Data, assessment and certification", href: "/assessments" },
  { icon: Wrench, label: "Services", desc: "Delivery & consulting", href: "/services" },
  { icon: ShoppingBag, label: "Care and Wellbeing Marketplace", desc: "Verified vendor marketplace", href: "/marketplace" },
  { icon: GraduationCap, label: "Experts", desc: "Domain expert advisory", href: "/expert-advisory" },
  { icon: Landmark, label: "Testbed", desc: "Living labs, AgeTech & scaling pilots", href: "/testbed-hub" },
];

export default function HeroSection({ onSignIn, onSignUp }: HeroSectionProps) {
  const navigate = useNavigate();
  const { session } = useAuth();

  return (
    <section className="relative w-full min-h-screen flex flex-col overflow-hidden bg-black">
      <img
        src={cityBg}
        alt=""
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        decoding="async"
        {...({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0.6)] via-[rgba(0,0,0,0.42)] to-[rgba(0,0,0,0.7)]" />
      <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 150px 60px rgba(0,0,0,0.45)' }} />

      {/* Top nav */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <span className="text-white text-lg font-bold tracking-tight">SeniorHub</span>
        <div className="flex items-center gap-2">
          {session ? (
            <Button
              size="sm"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-5 gap-2"
              onClick={() => navigate("/")}
            >
              <LayoutDashboard className="h-4 w-4" />
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full border border-white/20 text-white/90 bg-white/5 backdrop-blur-sm hover:bg-white/15 hover:text-white px-5"
                onClick={onSignIn}
              >
                Sign In
              </Button>
              <Button
                size="sm"
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-5"
                onClick={() => onSignUp ? onSignUp() : navigate("/auth?mode=signup")}
              >
                Create Account
              </Button>
            </>
          )}
        </div>
      </nav>

      {/* Centered content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pb-8">
        <p className="text-white/70 text-xs md:text-sm uppercase tracking-[0.28em] mb-5 font-medium">
          Powered by SAFE TRACKER AI
        </p>
        <h1 className="relative text-6xl md:text-8xl font-black tracking-[-0.02em] leading-none">
          <span className="absolute inset-0 text-white/20 blur-[1px]" aria-hidden="true">SeniorHub</span>
          <span className="relative bg-gradient-to-r from-white to-[#FFD89B] bg-clip-text text-transparent drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)]">SeniorHub</span>
        </h1>

        <div className="lip-intelligence" aria-label="Powered by Longevity Intelligence Platform">
          <div className="lip-intelligence__visual" aria-hidden="true">
            <span className="lip-intelligence__orbit lip-intelligence__orbit--outer" />
            <span className="lip-intelligence__orbit lip-intelligence__orbit--inner" />
            <span className="lip-intelligence__trace lip-intelligence__trace--left" />
            <span className="lip-intelligence__trace lip-intelligence__trace--right" />
            <span className="lip-intelligence__node lip-intelligence__node--one" />
            <span className="lip-intelligence__node lip-intelligence__node--two" />
            <span className="lip-intelligence__node lip-intelligence__node--three" />
            <span className="lip-intelligence__node lip-intelligence__node--four" />
            <span className="lip-intelligence__label lip-intelligence__label--assess">ASSESS</span>
            <span className="lip-intelligence__label lip-intelligence__label--insights">INSIGHTS</span>
            <span className="lip-intelligence__label lip-intelligence__label--guide">GUIDE</span>
            <span className="lip-intelligence__label lip-intelligence__label--impact">IMPACT</span>
          </div>
          <p className="lip-intelligence__title">
            <span>Powered by</span>
            <strong>LIP</strong>
          </p>
        </div>

        <p className="text-white/80 mt-3 text-lg md:text-xl max-w-xl leading-relaxed">
          From Assessment to Action for Age-Friendly Communities
        </p>

        <p className="text-white/45 mt-3 text-sm max-w-lg">
          Connecting institutions, cities, and solution providers to build age-friendly communities.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mt-14">
          <Button
            size="lg"
            className="bg-primary text-primary-foreground font-semibold px-8 shadow-[0_0_12px_hsl(var(--primary)/0.3)] hover:shadow-[0_0_18px_hsl(var(--primary)/0.5)] hover:scale-[1.02] transition-all duration-200"
            onClick={() => navigate("/assessments/new")}
            onMouseEnter={() => prefetch("/assessments/new")}
            onFocus={() => prefetch("/assessments/new")}
          >
            Start Assessment
          </Button>
          <Button
            size="lg"
            variant="ghost"
            className="border border-white/20 text-white bg-white/10 backdrop-blur-md hover:bg-white/20 hover:scale-[1.02] font-semibold px-8 transition-all duration-200"
            onClick={() => navigate("/explore")}
            onMouseEnter={() => prefetch("/explore")}
            onFocus={() => prefetch("/explore")}
          >
            Explore Platform
          </Button>
        </div>

        {/* Journey visualization — 5 pillars */}
        <div className="mt-16 w-full max-w-4xl">
          <div className="flex items-start justify-between gap-1">
            {journeySteps.map((step, i) => (
              <button
                key={step.label}
                type="button"
                onClick={() => navigate(step.href)}
                onMouseEnter={() => prefetch(step.href)}
                onFocus={() => prefetch(step.href)}
                className="flex-1 flex flex-col items-center text-center relative group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-lg p-1 hero-step-fade-in"
                style={{ animationDelay: `${600 + i * 150}ms` }}
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/[0.1] backdrop-blur-sm border border-white/20 flex items-center justify-center mb-2 transition-all group-hover:bg-white/20 group-hover:border-white/40 group-hover:scale-105">
                  <step.icon className="text-white/80 group-hover:text-white transition-colors" size={20} strokeWidth={1.5} />
                </div>
                <span className="text-white font-semibold text-xs md:text-sm">{step.label}</span>
                <span className="text-white/50 text-[10px] md:text-xs mt-0.5 max-w-[120px]">{step.desc}</span>
              </button>
            ))}
          </div>
          {/* Connecting arrows */}
          <div className="hidden sm:flex items-center justify-center -mt-[60px] md:-mt-[68px] pointer-events-none">
            {[0, 1, 2, 3].map((i) => (
              <React.Fragment key={i}>
                <div className="flex-1" />
                <ArrowRight className="text-white/30 mx-1" size={16} />
              </React.Fragment>
            ))}
            <div className="flex-1" />
          </div>
        </div>

      </div>
    </section>
  );
}

